/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/**
 * Plugins-rack definitions — port of the handoff's `pluginDefinitions` and
 * plugin-catalog content (Medaica_PCG_Engineering_Handoff_v0.8.1-dev.18,
 * `pcg_viewer_server.py` lines 1338–1353 and 1450–1463; unchanged upstream
 * through v0.8.3.18).
 *
 * The handoff's fixed "Fixture Result" rows are replaced by rows built from
 * the ACT and BeatAware results the heartbeat service stores
 * (`vendor-results.ts`), and the connection line names that live source
 * instead of "Fixture adapter · Live API not configured". The rest of each
 * definition is the handoff's text. The catalog is still an inert preview.
 */

interface PluginDefinition {
  name: string
  type: string
  scope: string
  connection: string
  input: string
  output: string
}

type PluginId = "act" | "beataware"

const PLUGIN_DEFINITIONS: { [id in PluginId]: PluginDefinition } = {
  act: {
    name: "ACT",
    type: "External analysis",
    scope: "Selected recording",
    connection: "Medaica heartbeat service · Live API",
    input: "Raw WAV + recording identifier",
    output: "Recording suitability + structural-sound classification",
  },
  beataware: {
    name: "BeatAware",
    type: "External analysis suite",
    scope: "Selected recording",
    connection: "Medaica heartbeat service · Live API",
    input: "Raw WAV + recording identifier",
    output: "Timing, rate, variation, phase-energy and structural-sound evidence",
  },
}

const PLUGIN_IDS = Object.keys(PLUGIN_DEFINITIONS) as PluginId[]

/** "Add Plugin" marketplace-preview content (inert; browsing/installation are outside the MVP). */
const PLUGIN_CATALOG: { section: string; items: { name: string; description: string; state: string }[] }[] = [
  {
    section: "Analysis",
    items: [
      { name: "ACT", description: "Structural Sound analysis", state: "Installed" },
      { name: "BeatAware", description: "External analysis suite", state: "Installed" },
    ],
  },
  {
    section: "Audio Processing",
    items: [
      { name: "Noise Reduction", description: "Parallel derived-audio pathway", state: "Requires integration" },
      { name: "Adaptive Filtering", description: "Configurable derived-audio pathway", state: "Requires integration" },
    ],
  },
  {
    section: "Workflow",
    items: [{ name: "Human-in-the-Loop Review", description: "External clinical review service", state: "Planned" }],
  },
]

export { PLUGIN_DEFINITIONS, PLUGIN_IDS, PLUGIN_CATALOG }
export type { PluginDefinition, PluginId }
